import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ExclamationTriangleIcon, 
  HomeIcon, 
  ArrowLeftIcon,
  ShieldExclamationIcon,
  ServerIcon 
} from '@heroicons/react/24/outline';

// 404 Not Found ページ
export const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-24 w-24 text-yellow-400" />
          <h1 className="mt-6 text-6xl font-bold text-gray-900">404</h1>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">ページが見つかりません</h2>
          <p className="mt-4 text-lg text-gray-600">
            お探しのページは存在しないか、移動した可能性があります。
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <button
            onClick={() => navigate(-1)}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            前のページに戻る
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <HomeIcon className="h-4 w-4 mr-2" />
            ホームページに戻る
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            問題が続く場合は、
            <a href="mailto:support@byters.jp" className="text-blue-600 hover:text-blue-500 ml-1">
              サポート
            </a>
            までお問い合わせください。
          </p>
        </div>
      </div>
    </div>
  );
};

// 403 Forbidden ページ
export const ForbiddenPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ShieldExclamationIcon className="mx-auto h-24 w-24 text-red-400" />
          <h1 className="mt-6 text-6xl font-bold text-gray-900">403</h1>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">アクセス権限がありません</h2>
          <p className="mt-4 text-lg text-gray-600">
            このページにアクセスする権限がありません。
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <button
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            ログインページに移動
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <HomeIcon className="h-4 w-4 mr-2" />
            ホームページに戻る
          </button>
        </div>
      </div>
    </div>
  );
};

// 500 Internal Server Error ページ
export const ServerErrorPage = () => {
  const navigate = useNavigate();

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ServerIcon className="mx-auto h-24 w-24 text-red-400" />
          <h1 className="mt-6 text-6xl font-bold text-gray-900">500</h1>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">サーバーエラー</h2>
          <p className="mt-4 text-lg text-gray-600">
            サーバーで問題が発生しました。しばらく時間をおいてから再度お試しください。
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <button
            onClick={handleReload}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            ページを再読み込み
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <HomeIcon className="h-4 w-4 mr-2" />
            ホームページに戻る
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            問題が続く場合は、
            <a href="mailto:support@byters.jp" className="text-blue-600 hover:text-blue-500 ml-1">
              サポート
            </a>
            までお問い合わせください。
          </p>
        </div>
      </div>
    </div>
  );
};

// エラーバウンダリーコンポーネント
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // 本番環境では適切なエラー監視サービスに送信
    if (process.env.NODE_ENV === 'production') {
      // TODO: Sentry, LogRocket, etc. にエラーを送信
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto h-24 w-24 text-red-400" />
              <h2 className="mt-6 text-3xl font-bold text-gray-900">予期しないエラーが発生しました</h2>
              <p className="mt-4 text-lg text-gray-600">
                アプリケーションでエラーが発生しました。ページを再読み込みしてください。
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                ページを再読み込み
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                ホームページに戻る
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-md">
                <h3 className="text-sm font-medium text-red-800">開発者情報:</h3>
                <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap">
                  {this.state.error?.toString()}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ネットワークエラー用トーストコンポーネント
export const NetworkErrorToast = ({ onRetry, onDismiss }) => {
  return (
    <div className="fixed top-4 right-4 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg max-w-sm">
      <div className="flex items-start">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">接続エラー</p>
          <p className="text-xs mt-1">サーバーに接続できませんでした。</p>
          <div className="mt-2 flex space-x-2">
            <button
              onClick={onRetry}
              className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
            >
              再試行
            </button>
            <button
              onClick={onDismiss}
              className="text-xs text-red-600 hover:text-red-500"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};